import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Controller } from "react-hook-form";
import { Textarea } from "~/components/ui/textarea";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import { FormField } from "~/components/ui/form-field";
import { ConditionalField } from "~/components/ui/conditional-field";
import { CheckboxGroupField } from "~/components/form/checkbox-group-field";
import {
  IdentifySpecificGoalsSchema,
  goalsField,
  otherGoalField,
  specificGoalsStep,
} from "~/lib/schemas/coach-discovery";
import type { IdentifySpecificGoalsBaseSchema } from "~/lib/schemas/coach-discovery";
import { useDiscoveryForm } from "~/lib/hooks/use-discovery-form";
import { createNoIndexMeta } from "~/lib/shared/meta";
import type { z } from "zod";

type IdentifySpecificGoalsData = z.infer<
  typeof IdentifySpecificGoalsBaseSchema
>;

function DiscoveryStep1() {
  const { form, handleNextStep } = useDiscoveryForm(
    IdentifySpecificGoalsSchema,
    1,
  );
  const { handleSubmit, control, watch, setFocus, formState } = form;
  const { errors, isSubmitting, isValid } = formState;

  const watchedGoals = watch("goals") || [];

  const onSubmit = (_data: IdentifySpecificGoalsData) => {
    handleNextStep();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiscoveryStep.Title title={specificGoalsStep.title} />
      <DiscoveryStep.Card>
        <FormField error={errors.goals?.message}>
          <CheckboxGroupField
            name="goals"
            control={control}
            options={goalsField.options || []}
            onTrigger={{
              Other: () => setFocus("otherGoal"),
            }}
          />
        </FormField>

        <ConditionalField show={watchedGoals.includes("Other")} indent>
          <FormField error={errors.otherGoal?.message}>
            <Controller
              name="otherGoal"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Textarea
                  value={field.value}
                  onValueChange={field.onChange}
                  className="focus:ring-0"
                  maxLength={otherGoalField.maxLength}
                  placeholder={otherGoalField.placeholder}
                />
              )}
            />
          </FormField>
        </ConditionalField>
      </DiscoveryStep.Card>
      <DiscoveryStep.Control
        type="submit"
        loading={isSubmitting}
        disabled={!isValid}
      />
    </form>
  );
}

export const Route = createFileRoute("/_public/discovery/steps/1/")({
  head: () =>
    createNoIndexMeta(
      "Step 1: Identify Your Goals – Dream Coach Match",
      "Identify your specific coaching goals in our coach discovery process",
    ),
  component: DiscoveryStep1,
});
